import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Tag, Tooltip } from "antd";
import { RxQuestionMarkCircled } from "react-icons/rx";
import PaystackPop from "@paystack/inline-js";

import { MutationKeys, QueryKeys } from "../../../enums/react-query";
import { useAppStore } from "../../../store/store";
import { TCurrency } from "../../../types/types";
import AccountRadio from "../../atoms/AccountRadio";
import {
  initiateDeposit,
  verifyDeposit,
} from "../../../services/deposit/deposit.service";
import { getDistributorDepositCurrencies } from "../../../services/currencies/currencies.service";
import { UsdcDepositForm } from "../../Accounts/UsdcDepositForm";
import { FiatDepositForm } from "../../Accounts/FiatDepositForm";
import { handleWalletAddress } from "../../../utils/helper";
import { toast } from "sonner";

const DepositDrawer = () => {
  const setIsDrawerOpen = useAppStore((state) => state.setIsDrawerOpen);

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const [activeFundType, setActiveFundType] = useState<"fiat" | "usdc">("fiat");
  const [selectedCurrency, setSelectedCurrency] = useState<
    TCurrency | undefined
  >();

  const fiatWithdrawalAmount = Form.useWatch("fiatAmount", form);
  const usdcWithdrawalAmount = Form.useWatch("usdcAmount", form);

  const changeActiveFundType = (value: "fiat" | "usdc") => {
    setActiveFundType(value);
  };

  const { data: depositCurrencies } = useQuery({
    queryKey: [QueryKeys.GETDISTRIBUTORDEPOSITCURRENCIES],
    queryFn: async () => getDistributorDepositCurrencies(),
  });

  const { isPending: isVerifyingDeposit, mutate: verifyDepositMutate } =
    useMutation({
      mutationKey: [MutationKeys.VERIFYDEPOSIT],
      mutationFn: (reference: string) =>
        verifyDeposit(reference, selectedCurrency?._id!),
      onSuccess() {
        setIsDrawerOpen(false);
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GETDISTRIBUTORFIATACCOUNTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GETDISTRIBUTORUSDCACCOUNTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GETAVAILABLEBALANCES],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GETDEPOSITSANDWITHDRAWALS],
        });
      },
    });

  const { isPending: isInitiatingDeposit, mutate: initiateDepositMutate } =
    useMutation({
      mutationKey: [MutationKeys.INITIATEDEPOSIT],
      mutationFn: (depositAmount: number) => initiateDeposit(depositAmount),
      onSuccess(data: {
        data: { payload: { access_code: string; reference: string } };
      }) {
        const paystackPopup = new PaystackPop();
        paystackPopup.resumeTransaction(data.data.payload?.access_code, {
          onSuccess(data: { reference: string }) {
            verifyDepositMutate(data.reference);
          },
        });
      },
    });

  const customizeRequiredMark = (label: any, prop: any) => (
    <>
      {prop?.required ? (
        <Tag color="error" className="Nunito">
          Required
        </Tag>
      ) : (
        <Tag color="warning" className="Nunito">
          optional
        </Tag>
      )}
      {label}
    </>
  );

  return (
    <div className="w-full h-full">
      <div className="w-full bg-brand-yellow h-[80px] md:h-[100px] flex items-center justify-center">
        <h5 className="text-[15px] 2xl:text-[20px] font-[600] text-brand-darkYellow uppercase">
          Deposit Funds
        </h5>
        <span>
          <Tooltip title="Funds deposited here will be credited to your distributor dashboard and can be used for managing bill payments.">
            <RxQuestionMarkCircled className="ml-1 text-brand-darkYellow" />
          </Tooltip>
        </span>
      </div>
      <div className="selectFundsType mt-[20px] px-[30px] md:px-[58px] flex flex-col gap-[3px]">
        <p className="text-[12px] font-[300] text-[#667085]">Funds type:</p>
        <div className="flex gap-4 uppercase">
          <AccountRadio
            value="fiat"
            activeFundType={activeFundType}
            changeActiveFundType={changeActiveFundType}
          />
          <AccountRadio
            value="usdc"
            activeFundType={activeFundType}
            changeActiveFundType={changeActiveFundType}
          />
        </div>
        <div className="w-full h-[1px] bg-[#E6EAEE] "></div>
      </div>

      <Form
        initialValues={{
          chain: "Stellar",
          walletAddress: handleWalletAddress(
            process.env.REACT_APP_EAZIPOWER_DEPOSIT_WALLET!,
            13,
            -4,
          ),
        }}
        name="deposit"
        form={form}
        // onFinish={onDepositFinish}
        layout="vertical"
        autoComplete="off"
        requiredMark={customizeRequiredMark}
        className="Nunito h-fit pt-[15px] pb-[15px] md:pb-[29px] px-[30px] md:px-[58px] rounded-[8px] bg-white flex flex-col w-full gap-[24px] "
      >
        {activeFundType === "fiat" ? (
          <FiatDepositForm
            currencies={depositCurrencies?.data.payload}
            setSelectedCurrency={setSelectedCurrency}
            selectedCurrency={selectedCurrency}
          />
        ) : (
          <UsdcDepositForm currencies={depositCurrencies?.data.payload} />
        )}

        {activeFundType === "fiat" ? (
          <div className="flex gap-[14px] items-center justify-end">
            <Button
              type="primary"
              htmlType="button"
              className="Nunito w-full max-w-[127px] h-[40px] flex items-center justify-center bg-[#EFEFEF] hover:!bg-[#EFEFEF] !text-[#6B7280] hover:!text-[#6B7280] hover:opacity-[0.8] font-[600] text-[14px] 2xl:text-[16px] rounded-[8px]"
              loading={false}
              onClick={() => setIsDrawerOpen(false)}
            >
              Cancel
            </Button>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="Nunito w-fit min-w-[120px] h-[40px] flex items-center justify-center bg-brand-darkYellow hover:!bg-brand-darkYellow disabled:hover:!bg-brand-lightGrey hover:opacity-[0.8] font-[600] text-[14px] 2xl:text-[16px] rounded-[8px]  "
                loading={isInitiatingDeposit || isVerifyingDeposit}
                onClick={() => {
                  if (fiatWithdrawalAmount > 0) {
                    initiateDepositMutate(fiatWithdrawalAmount);
                  }
                }}
              >
                Proceed
              </Button>
            </Form.Item>
          </div>
        ) : (
          <div className="flex gap-[14px] items-center justify-center"></div>
        )}
      </Form>
    </div>
  );
};

export default DepositDrawer;
